/**
 * Area Services
 * 
 * Services for fetching dynamic area data including
 * weather, Wikipedia content, and more.
 */

import type { AreaWeatherData, AreaWikiData } from './area-types';

/**
 * Fetch real-time weather data from Open-Meteo API
 * 
 * @param latitude - Latitude in decimal degrees
 * @param longitude - Longitude in decimal degrees
 * @returns Weather data or null if fetch fails
 */
export async function fetchAreaWeather(
  latitude: number,
  longitude: number
): Promise<AreaWeatherData | null> {
  try {
    const response = await fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,relative_humidity_2m,wind_speed_10m,wind_direction_10m&timezone=auto`,
      { next: { revalidate: 1800 } } // Cache for 30 minutes
    );

    if (!response.ok) return null;

    const data = await response.json();

    if (!data.current) return null;

    return {
      temperature: Math.round(data.current.temperature_2m),
      humidity: data.current.relative_humidity_2m,
      windSpeed: Math.round(data.current.wind_speed_10m),
      windDirection: data.current.wind_direction_10m,
    };
  } catch (error) {
    console.error('Failed to fetch weather:', error);
    return null;
  }
}

/**
 * Convert wind direction in degrees to cardinal direction
 * 
 * @param degrees - Wind direction in degrees
 * @returns Cardinal direction (N, NE, E, etc.)
 */
export function getCardinalDirection(degrees: number): string {
  const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  const index = Math.round(degrees / 45) % 8;
  return directions[index] ?? 'N';
}

/**
 * Fetch Wikipedia data for an area
 * 
 * Tries multiple search strategies to find the best match:
 * 1. "[Area] Beach Mauritius" - most likely to have scenic photo
 * 2. "[Area] Mauritius" - standard fallback
 * 
 * @param areaName - Name of the area
 * @returns Wiki data or null if not found
 */
export async function fetchAreaWikiData(areaName: string): Promise<AreaWikiData | null> {
  const fetchFromApi = async (searchTerm: string): Promise<AreaWikiData | null> => {
    try {
      // Search for the article
      const searchUrl = `https://en.wikipedia.org/w/api.php?action=query&list=search&srsearch=${encodeURIComponent(searchTerm)}&format=json&origin=*`;
      const searchRes = await fetch(searchUrl, { next: { revalidate: 86400 } }); // Cache for 24 hours
      const searchJson = await searchRes.json();

      if (!searchJson.query?.search || searchJson.query.search.length === 0) {
        return null;
      }

      const title = searchJson.query.search[0].title;

      // Get article content and image
      const contentUrl = `https://en.wikipedia.org/w/api.php?action=query&prop=extracts|pageimages&piprop=thumbnail&pithumbsize=1600&exintro&explaintext&titles=${encodeURIComponent(title)}&format=json&origin=*`;
      const contentRes = await fetch(contentUrl, { next: { revalidate: 86400 } });
      const contentJson = await contentRes.json();

      const pages = contentJson.query.pages;
      const pageId = Object.keys(pages)[0];

      if (!pageId || pageId === '-1') return null;

      const page = pages[pageId];
      if (!page) return null;

      return {
        title: page.title,
        extract: page.extract,
        url: `https://en.wikipedia.org/wiki/${encodeURIComponent(page.title)}`,
        imageUrl: page.thumbnail?.source,
      };
    } catch (error) {
      console.error('Wiki fetch error:', error);
      return null;
    }
  };

  // Try multiple search strategies
  const queries = [
    `${areaName} Beach Mauritius`,
    `${areaName} Mauritius`,
    `${areaName}`,
  ];

  let bestResult: AreaWikiData | null = null;

  for (const query of queries) {
    const result = await fetchFromApi(query);
    if (result) {
      // If we found a result with an image, return it immediately
      if (result.imageUrl) {
        return result;
      }
      // Store as fallback if no image
      if (!bestResult) {
        bestResult = result;
      }
    }
  }

  return bestResult;
}

/**
 * Parse DMS coordinates string to decimal degrees
 * 
 * @param coordinates - DMS string (e.g., "20° 00' 50\" S, 57° 34' 56\" E")
 * @returns Tuple of [latitude, longitude] or null if parsing fails
 */
export function parseCoordinates(coordinates: string): [number, number] | null {
  try {
    const regex = /(\d+)[°\s]+(\d+)['\s]+(\d+)["]?\s*([NSEW])/gi;
    const matches = [...coordinates.matchAll(regex)];

    if (matches.length >= 2) {
      const match0 = matches[0];
      const match1 = matches[1];
      
      if (!match0 || !match1) return null;
      
      const d0 = match0[1], m0 = match0[2], s0 = match0[3], dir0 = match0[4];
      const d1 = match1[1], m1 = match1[2], s1 = match1[3], dir1 = match1[4];
      
      if (!d0 || !m0 || !s0 || !dir0 || !d1 || !m1 || !s1 || !dir1) return null;
      
      const toDec = (d: string, m: string, s: string, dir: string): number => {
        let dec = parseFloat(d) + parseFloat(m) / 60 + parseFloat(s) / 3600;
        if (dir.toUpperCase() === 'S' || dir.toUpperCase() === 'W') dec *= -1;
        return dec;
      };

      const lat = toDec(d0, m0, s0, dir0);
      const lng = toDec(d1, m1, s1, dir1);

      return [lat, lng];
    }
  } catch (e) {
    console.error('Failed to parse coordinates:', e);
  }

  return null;
}

/**
 * Format coordinates for display
 * 
 * @param latitude - Latitude in decimal degrees
 * @param longitude - Longitude in decimal degrees
 * @returns Formatted DMS string
 */
export function formatCoordinates(latitude: number, longitude: number): string {
  const toDMS = (value: number, isLat: boolean): string => {
    const direction = isLat ? (value >= 0 ? 'N' : 'S') : (value >= 0 ? 'E' : 'W');
    const absValue = Math.abs(value);
    const degrees = Math.floor(absValue);
    const minutes = Math.floor((absValue - degrees) * 60);
    const seconds = Math.round(((absValue - degrees) * 60 - minutes) * 60);
    
    return `${degrees}° ${minutes.toString().padStart(2, '0')}' ${seconds.toString().padStart(2, '0')}" ${direction}`;
  };

  return `${toDMS(latitude, true)}, ${toDMS(longitude, false)}`;
}

/**
 * Generate default market stats for an area
 * These are placeholder stats that can be replaced with real data
 */
export function getDefaultMarketStats() {
  return {
    averageYield: '4.2%',
    growth: {
      period: '5Y',
      percentage: '+45%',
      trend: 'up' as const,
    },
    priceHistory: [
      { year: '2019', price: 1.0, formatted: '$1.00M' },
      { year: '2020', price: 1.12, formatted: '$1.12M' },
      { year: '2021', price: 1.08, formatted: '$1.08M' },
      { year: '2022', price: 1.25, formatted: '$1.25M' },
      { year: '2023', price: 1.38, formatted: '$1.38M' },
      { year: '2024', price: 1.45, formatted: '$1.45M' },
    ],
  };
}
